import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';

const ICN = {
    x: (
        <svg viewBox="0 0 14 14" width="14" height="14" fill="none">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    check: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
};

const CONNECTORS = [
    { key: 'googledrive', name: 'Google Drive', cls: 'gd', letter: 'G', bg: '#ffffff', fg: '#1a73e8', cat: 'Storage', kind: 'folders', blurb: 'Sync source files from Drive folders.' },
    { key: 'dropbox', name: 'Dropbox', cls: 'db', letter: 'D', bg: '#0061FF', fg: '#ffffff', cat: 'Storage', kind: 'files', blurb: 'Pull files from any shared folder.' },
    { key: 'onedrive', name: 'OneDrive', cls: 'od', letter: 'O', bg: '#0078D4', fg: '#ffffff', cat: 'Storage', kind: 'folders', blurb: 'Microsoft 365 file sync.' },
    { key: 'slack', name: 'Slack', cls: 'sl', letter: 'S', bg: '#4A154B', fg: '#ffffff', cat: 'Communication', kind: 'channels', blurb: 'Notifications and slash commands.' },
    { key: 'figma', name: 'Figma', cls: 'fg', letter: 'F', bg: '#1a1a1a', fg: '#F24E1E', cat: 'Design', kind: 'files', blurb: 'Translate strings in design files.' },
    { key: 'github', name: 'GitHub', cls: 'gh', letter: 'G', bg: '#1a1a1a', fg: '#ffffff', cat: 'Code', kind: 'repos', blurb: 'String files committed to a repo.' },
    { key: 'gitlab', name: 'GitLab', cls: 'gl', letter: 'G', bg: '#ffffff', fg: '#FC6D26', cat: 'Code', kind: 'repos', blurb: 'Self-hosted & cloud repos.' },
    { key: 'notion', name: 'Notion', cls: 'no', letter: 'N', bg: '#ffffff', fg: '#1a1a1a', cat: 'Project mgmt', kind: 'pages', blurb: 'Pages, databases, and wikis.' },
    { key: 'contentful', name: 'Contentful', cls: 'cf', letter: 'C', bg: '#0681A4', fg: '#ffffff', cat: 'CMS', kind: 'spaces', blurb: 'Locale-aware content trees.' },
    { key: 'linear', name: 'Linear', cls: 'ln', letter: 'L', bg: '#5E6AD2', fg: '#ffffff', cat: 'Project mgmt', kind: 'teams', blurb: 'Translation tickets per language.' },
    { key: 'webflow', name: 'Webflow', cls: 'wf', letter: 'W', bg: '#146EF5', fg: '#ffffff', cat: 'CMS', kind: 'collections', blurb: 'Collections via the Data API.' },
    { key: 'sanity', name: 'Sanity', cls: 'sn', letter: 'S', bg: '#F03E2F', fg: '#ffffff', cat: 'CMS', kind: 'datasets', blurb: 'Studio plugin + GROQ queries.' },
];

const CATEGORIES = ['All', 'Storage', 'Code', 'Design', 'CMS', 'Communication', 'Project mgmt'];

// ----------------------------------------------------------------------------
// Per-connector configuration schemas. Each "coded" connector exposes the
// controls that are actually unique to that service (Drives + Google formats
// for gdrive; SharePoint + sensitivity labels for OneDrive; PR strategy +
// i18n glob for GitHub; channel notifications for Slack; etc.).
//
// Field types: toggle, select, text, segmented, list (toggleable items).
// ----------------------------------------------------------------------------
const SYNC_FREQ_OPTS = [
    { value: 'realtime', label: 'Real-time (webhook)' },
    { value: '15m', label: 'Every 15 min' },
    { value: '1h', label: 'Hourly' },
    { value: '4h', label: 'Every 4 hours' },
    { value: 'daily', label: 'Daily' },
    { value: 'manual', label: 'Manual only' },
];

const CONNECTOR_CONFIG = {
    googledrive: {
        sections: [
            {
                title: 'Drives',
                desc: 'Which Drives Rush Studio can browse for content.',
                fields: [
                    { type: 'gdrive-drives', key: 'drives' },
                    { type: 'toggle', key: 'sharedWithMe', label: 'Include "Shared with me"', desc: 'Watch files others have shared directly with this account.', on: false },
                ],
            },
            {
                title: 'Folders to watch',
                desc: 'Pick the folders Rush Studio imports translatable files from. Drives must be enabled above.',
                fields: [
                    { type: 'gdrive-folders', key: 'folders' },
                ],
            },
            {
                title: 'Google formats',
                desc: 'Native Google files are converted on import so they round-trip cleanly.',
                fields: [
                    { type: 'select', key: 'docExport', label: 'Export Docs as', desc: 'Google Docs are converted to this editable format.', on: 'docx',
                      options: [{ value: 'docx', label: 'Word (.docx)' }, { value: 'odt', label: 'OpenDocument (.odt)' }] },
                    { type: 'select', key: 'gridExport', label: 'Export Sheets & Slides as', desc: 'Applies to Google Sheets and Slides.', on: 'office',
                      options: [{ value: 'office', label: 'Office (.xlsx / .pptx)' }, { value: 'open', label: 'OpenDocument' }] },
                    { type: 'toggle', key: 'comments', label: 'Import comments as context', desc: 'Show Google Doc comments to translators as reference notes.', on: true },
                ],
            },
            {
                title: 'Sync',
                fields: [
                    { type: 'select', key: 'freq', label: 'Sync frequency', desc: 'How often Rush Studio pulls upstream changes.', on: '4h', options: SYNC_FREQ_OPTS },
                    { type: 'toggle', key: 'pushBack', label: 'Push translations back', desc: 'Write completed translations back to Drive.', on: true },
                    { type: 'select', key: 'writeBack', label: 'Write translations as', desc: 'Where finished translations land in Drive.', on: 'revision',
                      options: [
                          { value: 'revision', label: 'New revision of the original' },
                          { value: 'suffix', label: 'Copy with –[lang] suffix' },
                          { value: 'folder', label: 'Into a /Translations folder' },
                      ], dependsOn: 'pushBack' },
                ],
            },
        ],
    },

    dropbox: {
        sections: [
            {
                title: 'Spaces',
                desc: 'Which Dropbox space Rush Studio syncs from.',
                fields: [
                    { type: 'list', key: 'spaces', items: [], emptyHint: 'No spaces selected yet.' },
                ],
            },
            {
                title: 'Dropbox content',
                desc: 'How Rush Studio treats Dropbox-specific file types.',
                fields: [
                    { type: 'toggle', key: 'paper', label: 'Translate Paper docs', desc: 'Include Dropbox Paper documents alongside files.', on: true },
                    { type: 'select', key: 'conflict', label: 'On edit conflict', desc: 'If a source file changes mid-translation.', on: 'keep',
                      options: [{ value: 'keep', label: 'Keep both (conflicted copy)' }, { value: 'reimport', label: 'Re-import & flag' }] },
                    { type: 'toggle', key: 'links', label: 'Follow shared-folder links', desc: 'Sync folders shared into this account by others.', on: false },
                ],
            },
            {
                title: 'Sync',
                fields: [
                    { type: 'select', key: 'freq', label: 'Sync frequency', desc: 'How often Rush Studio pulls upstream changes.', on: '1h', options: SYNC_FREQ_OPTS },
                    { type: 'toggle', key: 'pushBack', label: 'Push translations back', desc: 'Write completed translations back to Dropbox.', on: true },
                    { type: 'select', key: 'writeBack', label: 'Save translations to', desc: 'Where finished translations are written.', on: 'sub',
                      options: [
                          { value: 'same', label: 'Same folder as the source' },
                          { value: 'sub', label: '/Translations subfolder' },
                          { value: 'mirror', label: 'Mirror under /Localized' },
                      ], dependsOn: 'pushBack' },
                ],
            },
        ],
    },

    onedrive: {
        sections: [
            {
                title: 'Sources',
                desc: 'OneDrive files and SharePoint sites Rush Studio can read.',
                fields: [
                    { type: 'list', key: 'sources', items: [], emptyHint: 'No sources selected yet.' },
                ],
            },
            {
                title: 'Compliance',
                desc: 'Respect your Microsoft 365 governance while syncing.',
                fields: [
                    { type: 'toggle', key: 'labels', label: 'Respect sensitivity labels', desc: 'Skip files at or above the selected label.', on: true },
                    { type: 'select', key: 'labelLevel', label: 'Skip at or above', desc: 'Microsoft Purview sensitivity level.', on: 'confidential',
                      options: [
                          { value: 'internal', label: 'Internal' },
                          { value: 'confidential', label: 'Confidential' },
                          { value: 'highly', label: 'Highly Confidential' },
                      ], dependsOn: 'labels' },
                    { type: 'toggle', key: 'checkout', label: 'Check out files while translating', desc: "Locks the source so co-authors can't overwrite mid-job.", on: false },
                ],
            },
            {
                title: 'Sync',
                fields: [
                    { type: 'select', key: 'freq', label: 'Sync frequency', desc: 'How often Rush Studio pulls upstream changes.', on: '4h', options: SYNC_FREQ_OPTS },
                    { type: 'toggle', key: 'pushBack', label: 'Push translations back', desc: 'Write completed translations back to OneDrive.', on: true },
                    { type: 'select', key: 'writeBack', label: 'Save translations to', desc: 'Where finished translations are written.', on: 'same',
                      options: [
                          { value: 'same', label: 'Same document library' },
                          { value: 'lib', label: 'Dedicated Translations library' },
                      ], dependsOn: 'pushBack' },
                ],
            },
        ],
    },

    slack: {
        sections: [
            {
                title: 'Channels',
                desc: 'Where Rush Studio posts updates and accepts slash commands. Pulled from your workspace after authorization.',
                fields: [
                    { type: 'list', key: 'channels', items: [], emptyHint: 'No channels selected yet.' },
                    { type: 'text', key: 'defaultChannel', label: 'Default channel', desc: 'Channel name where unrouted alerts land.', on: '', placeholder: '#localization' },
                ],
            },
            {
                title: 'Notifications',
                desc: 'Pick which events trigger a Slack message.',
                fields: [
                    { type: 'toggle', key: 'nProject', label: 'Project status changes', desc: 'New project, completed, paused.', on: true },
                    { type: 'toggle', key: 'nReview', label: 'Review requests', desc: 'Tag the reviewer when their queue grows.', on: true },
                    { type: 'toggle', key: 'nSlip', label: 'Deadline slip alerts', desc: 'Posts when an estimated due date is at risk.', on: true },
                    { type: 'toggle', key: 'nMention', label: 'Mentions on segments', desc: 'Send a DM when someone @-mentions you in a comment.', on: true },
                    { type: 'segmented', key: 'digest', label: 'Daily digest', desc: 'Round-up of activity at end of day.', on: 'off',
                      options: [{ value: 'off', label: 'Off' }, { value: '6pm', label: '6pm local' }, { value: 'eow', label: 'End of week' }] },
                ],
            },
            {
                title: 'Slash commands',
                fields: [
                    { type: 'toggle', key: 'cmdEnabled', label: 'Enable /rush commands', desc: 'Lets teammates run /rush status, /rush assign from any channel.', on: true },
                    { type: 'toggle', key: 'cmdRestrict', label: 'Restrict to admins', desc: 'Only workspace admins can invoke commands.', on: false, dependsOn: 'cmdEnabled' },
                ],
            },
        ],
    },

    figma: {
        sections: [
            {
                title: 'Files & libraries',
                desc: 'Which Figma files Rush Studio watches for text changes.',
                fields: [
                    { type: 'list', key: 'files', items: [], emptyHint: 'No files selected yet.' },
                    { type: 'toggle', key: 'libs', label: 'Include shared libraries', desc: 'Pull text styles and components from team libraries.', on: false },
                ],
            },
            {
                title: 'Scope',
                desc: 'Limit which text layers are translated.',
                fields: [
                    { type: 'segmented', key: 'layerScope', label: 'Layer scope', desc: 'Translate every text node, or only marked ones.', on: 'marked',
                      options: [{ value: 'all', label: 'All text' }, { value: 'marked', label: 'Marked frames' }, { value: 'page', label: 'Specific pages' }] },
                    { type: 'toggle', key: 'preserveStyles', label: 'Preserve text styles', desc: 'Keep font, size, and color when writing back translations.', on: true },
                    { type: 'toggle', key: 'autoResize', label: 'Auto-resize frames', desc: 'Expand auto-layout frames if translated text overflows.', on: true },
                ],
            },
            {
                title: 'Sync',
                fields: [
                    { type: 'select', key: 'freq', label: 'Sync frequency', desc: 'How often Rush Studio polls Figma for file changes.', on: 'realtime', options: SYNC_FREQ_OPTS },
                    { type: 'toggle', key: 'pushBack', label: 'Write translations back', desc: 'Create a translated variant of each frame in the source file.', on: true },
                    { type: 'select', key: 'variantStrategy', label: 'Translation variants', desc: 'How translated copies appear in the file.', on: 'page',
                      options: [
                          { value: 'page', label: 'New page per language' },
                          { value: 'frame', label: 'Duplicate frame, language suffix' },
                          { value: 'branch', label: 'Figma branch per language' },
                      ], dependsOn: 'pushBack' },
                ],
            },
        ],
    },

    github: {
        sections: [
            {
                title: 'Repositories',
                desc: 'Which repos contain i18n / string files to translate.',
                fields: [
                    { type: 'list', key: 'repos', items: [], emptyHint: 'No repositories selected yet.' },
                    { type: 'text', key: 'baseBranch', label: 'Base branch', desc: 'Branch Rush Studio reads from (per-repo override available).', on: 'main', placeholder: 'main' },
                ],
            },
            {
                title: 'Files & format',
                desc: 'Tell Rush Studio where the translatable strings live.',
                fields: [
                    { type: 'text', key: 'glob', label: 'Source glob', desc: 'Glob pattern for files Rush Studio should read.', on: '', placeholder: 'locales/en/**/*.json' },
                    { type: 'select', key: 'format', label: 'String file format', desc: 'How keys are parsed and re-emitted.', on: 'json',
                      options: [
                          { value: 'json', label: 'JSON (i18next, react-intl)' },
                          { value: 'yaml', label: 'YAML' },
                          { value: 'po', label: 'Gettext (.po / .pot)' },
                          { value: 'xliff', label: 'XLIFF 2.x' },
                          { value: 'strings', label: 'Apple .strings / .stringsdict' },
                          { value: 'arb', label: 'Flutter .arb' },
                      ] },
                    { type: 'text', key: 'targetPath', label: 'Target path template', desc: '{lang} is replaced per target locale.', on: '', placeholder: 'locales/{lang}/' },
                ],
            },
            {
                title: 'Pull requests',
                desc: 'How Rush Studio opens PRs back to your repo.',
                fields: [
                    { type: 'segmented', key: 'prStrategy', label: 'PR strategy', desc: 'One PR per language, or one rolling PR.', on: 'perLang',
                      options: [{ value: 'perLang', label: 'PR per language' }, { value: 'rolling', label: 'Single rolling PR' }, { value: 'direct', label: 'Commit to base' }] },
                    { type: 'text', key: 'prLabels', label: 'PR labels', desc: 'Comma-separated labels applied to translation PRs.', on: '', placeholder: 'localization,automated' },
                    { type: 'text', key: 'commitMsg', label: 'Commit message', desc: 'Supports {lang} and {count} placeholders.', on: 'i18n({lang}): update translations', placeholder: 'i18n({lang}): update translations' },
                    { type: 'toggle', key: 'requireReview', label: 'Require human review', desc: 'Mark PRs as draft until a translator approves.', on: true },
                    { type: 'toggle', key: 'signCommits', label: 'GPG-sign commits', desc: 'Sign commits with the configured Rush bot key.', on: false },
                ],
            },
        ],
    },

    gitlab: {
        sections: [
            {
                title: 'Instance',
                desc: 'Cloud or self-hosted GitLab.',
                fields: [
                    { type: 'text', key: 'host', label: 'Instance URL', desc: 'Leave as gitlab.com for hosted GitLab.', on: 'https://gitlab.com', placeholder: 'https://gitlab.example.com' },
                    { type: 'text', key: 'group', label: 'Default group / namespace', desc: 'Projects under this group are listed first.', on: '', placeholder: 'your-group' },
                ],
            },
            {
                title: 'Projects',
                desc: 'GitLab projects Rush Studio reads strings from.',
                fields: [
                    { type: 'list', key: 'projects', items: [], emptyHint: 'No projects selected yet.' },
                    { type: 'text', key: 'glob', label: 'Source glob', desc: 'Pattern for translatable string files.', on: '', placeholder: 'i18n/en/**/*.yml' },
                ],
            },
            {
                title: 'Merge requests',
                fields: [
                    { type: 'segmented', key: 'mrStrategy', label: 'MR strategy', desc: 'How translations are proposed.', on: 'perLang',
                      options: [{ value: 'perLang', label: 'MR per language' }, { value: 'rolling', label: 'Single rolling MR' }] },
                    { type: 'toggle', key: 'pipeline', label: 'Wait for pipeline', desc: 'Only merge when CI is green.', on: true },
                    { type: 'toggle', key: 'squash', label: 'Squash on merge', desc: 'Combine all translation commits into one.', on: true },
                ],
            },
        ],
    },

    notion: {
        sections: [
            {
                title: 'Pages & databases',
                desc: 'Notion pages Rush Studio can read. Share each one with the integration first.',
                fields: [
                    { type: 'list', key: 'pages', items: [], emptyHint: 'No pages shared with the integration yet.' },
                    { type: 'toggle', key: 'subpages', label: 'Include sub-pages', desc: 'Recursively pull every child page.', on: true },
                ],
            },
            {
                title: 'Content handling',
                fields: [
                    { type: 'toggle', key: 'translateProps', label: 'Translate database properties', desc: 'Localize title, select options, and rich-text fields.', on: true },
                    { type: 'toggle', key: 'translateComments', label: 'Surface page comments', desc: 'Pull Notion comments as translator context.', on: false },
                    { type: 'segmented', key: 'destination', label: 'Localized output', desc: 'Where translated pages are created.', on: 'mirror',
                      options: [{ value: 'mirror', label: 'Mirror page tree' }, { value: 'prop', label: 'Same page, language property' }] },
                ],
            },
            {
                title: 'Sync',
                fields: [
                    { type: 'select', key: 'freq', label: 'Sync frequency', desc: 'How often Rush Studio polls Notion.', on: '1h', options: SYNC_FREQ_OPTS },
                ],
            },
        ],
    },

    contentful: {
        sections: [
            {
                title: 'Space & environment',
                fields: [
                    { type: 'text', key: 'spaceId', label: 'Space ID', desc: 'Find this under Settings → General.', on: '', placeholder: 'e.g. abc123xyz' },
                    { type: 'select', key: 'env', label: 'Environment', desc: 'Master, or a sandbox alias.', on: 'master',
                      options: [{ value: 'master', label: 'master' }, { value: 'staging', label: 'staging' }, { value: 'preview', label: 'preview' }] },
                    { type: 'text', key: 'webhook', label: 'Webhook secret', desc: 'Set this in Contentful → Webhooks for real-time sync.', on: '', placeholder: 'whsec_…' },
                ],
            },
            {
                title: 'Content types',
                desc: 'Pick which content types to translate.',
                fields: [
                    { type: 'list', key: 'types', items: [], emptyHint: 'No content types selected yet.' },
                    { type: 'toggle', key: 'fallback', label: 'Use default locale as fallback', desc: 'Empty fields fall back to the source locale while translation is in progress.', on: true },
                ],
            },
            {
                title: 'Publishing',
                fields: [
                    { type: 'segmented', key: 'publish', label: 'Publish strategy', desc: 'What happens when a translation is approved.', on: 'draft',
                      options: [{ value: 'draft', label: 'Save as draft' }, { value: 'publish', label: 'Auto-publish' }, { value: 'schedule', label: 'Schedule release' }] },
                ],
            },
        ],
    },

    linear: {
        sections: [
            {
                title: 'Teams',
                desc: 'Linear teams where Rush Studio creates localization tickets.',
                fields: [
                    { type: 'list', key: 'teams', items: [], emptyHint: 'No teams selected yet.' },
                    { type: 'text', key: 'defaultTeamKey', label: 'Default team key', desc: 'Linear team key (e.g. ENG) where unrouted tickets land.', on: '', placeholder: 'LOC' },
                ],
            },
            {
                title: 'Ticket shape',
                fields: [
                    { type: 'segmented', key: 'granularity', label: 'Granularity', desc: 'One ticket per language, or per project.', on: 'perLang',
                      options: [{ value: 'perProject', label: 'Per project' }, { value: 'perLang', label: 'Per language' }, { value: 'perFile', label: 'Per source file' }] },
                    { type: 'text', key: 'labels', label: 'Auto-applied labels', desc: 'Comma-separated.', on: '', placeholder: 'translation,rush' },
                    { type: 'select', key: 'priority', label: 'Default priority', on: 'medium',
                      options: [{ value: 'urgent', label: 'Urgent' }, { value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' }, { value: 'low', label: 'Low' }, { value: 'none', label: 'No priority' }] },
                ],
            },
            {
                title: 'Status mapping',
                desc: 'Map Rush Studio job states to Linear workflow states.',
                fields: [
                    { type: 'text', key: 'stateInProgress', label: 'In progress →', desc: 'Linear workflow state name.', on: 'In Progress', placeholder: 'In Progress' },
                    { type: 'text', key: 'stateReview', label: 'In review →', on: 'In Review', placeholder: 'In Review' },
                    { type: 'text', key: 'stateDone', label: 'Delivered →', on: 'Done', placeholder: 'Done' },
                    { type: 'toggle', key: 'closeOnDelivery', label: 'Close ticket on delivery', desc: 'Mark ticket as Done when all locales are delivered.', on: true },
                ],
            },
        ],
    },

    webflow: {
        sections: [
            {
                title: 'Site',
                fields: [
                    { type: 'text', key: 'siteId', label: 'Webflow site ID', desc: 'Find this in Project settings → General.', on: '', placeholder: '6123abc…' },
                    { type: 'text', key: 'sourceLocale', label: 'Source locale', desc: 'Locale code that holds the canonical copy.', on: 'en', placeholder: 'en' },
                ],
            },
            {
                title: 'Collections',
                desc: 'Which Collections Rush Studio reads via the Data API.',
                fields: [
                    { type: 'list', key: 'collections', items: [], emptyHint: 'No collections selected yet.' },
                    { type: 'toggle', key: 'staticPages', label: 'Include static pages', desc: 'Sync Pages designed in the Webflow Designer.', on: true },
                ],
            },
            {
                title: 'Publishing',
                fields: [
                    { type: 'segmented', key: 'publish', label: 'Publish target', desc: 'Where Rush Studio writes translated content.', on: 'staging',
                      options: [{ value: 'staging', label: 'Staging only' }, { value: 'live', label: 'Live site' }, { value: 'both', label: 'Both' }] },
                    { type: 'toggle', key: 'autoPublish', label: 'Auto-publish on approval', desc: 'Publish translated items when reviewed.', on: false },
                ],
            },
        ],
    },

    sanity: {
        sections: [
            {
                title: 'Project',
                fields: [
                    { type: 'text', key: 'projectId', label: 'Project ID', desc: 'Find this in your Sanity Studio config.', on: '', placeholder: 'a1b2c3' },
                    { type: 'text', key: 'dataset', label: 'Dataset', desc: 'production, staging, or custom.', on: 'production', placeholder: 'production' },
                    { type: 'select', key: 'apiVersion', label: 'API version', on: 'v2024-01-01',
                      options: [{ value: 'v2024-01-01', label: 'v2024-01-01' }, { value: 'v2023-10-01', label: 'v2023-10-01' }] },
                ],
            },
            {
                title: 'Schema types',
                desc: 'GROQ-filtered document types Rush Studio translates.',
                fields: [
                    { type: 'list', key: 'types', items: [], emptyHint: 'No schema types selected yet.' },
                    { type: 'text', key: 'groqFilter', label: 'Additional GROQ filter', desc: 'Optional. Narrow which documents are picked up.', on: '', placeholder: '&& !(_id in path("drafts.**"))' },
                ],
            },
            {
                title: 'Localization strategy',
                fields: [
                    { type: 'segmented', key: 'strategy', label: 'Strategy', desc: 'How locale variants are stored.', on: 'field',
                      options: [{ value: 'field', label: 'Locale fields on same doc' }, { value: 'doc', label: 'One document per locale' }] },
                    { type: 'toggle', key: 'studioPlugin', label: 'Install Studio plugin', desc: 'Adds a translation panel inside Sanity Studio.', on: true },
                ],
            },
        ],
    },
};

// OAuth endpoints per connector. Each returns { url } pointing at the
// provider's consent screen; the frontend redirects the browser to it.
// `tokenInQuery: true` connectors need the user JWT echoed back to the
// backend via the OAuth `state` param (GitHub does this).
const AUTH_ENDPOINTS = {
    googledrive: { path: '/api/integrations/google/auth' },
    onedrive: { path: '/api/integrations/onedrive/auth' },
    dropbox: { path: '/api/dropbox/auth' },
    slack: { path: '/api/integrations/slack/auth' },
    github: { path: '/api/integrations/github/auth', tokenInQuery: true },
    // Figma uses a personal API key generated by the user, not OAuth. The
    // drawer special-cases this kind below — no consent-screen redirect.
    figma: { kind: 'apikey' },
};

// Generic config for connectors not yet wired up — keeps the drawer functional.
const FALLBACK_CONFIG = {
    sections: [
        {
            title: 'Sync',
            fields: [
                { type: 'select', key: 'freq', label: 'Sync frequency', desc: 'How often Rush Studio checks for changes.', on: '4h', options: SYNC_FREQ_OPTS },
                { type: 'toggle', key: 'twoWay', label: 'Two-way sync', desc: 'Push approved translations back to the source.', on: true },
                { type: 'toggle', key: 'pretranslate', label: 'Pre-translate on import', desc: 'Run MT on new files automatically.', on: true },
            ],
        },
    ],
};

const SourceTile = ({ c, size }) => (
    <span
        className={`src-tile ${size === 'lg' ? 'lg' : ''}`}
        style={{
            background: c.bg,
            color: c.fg,
            border: c.bg.toLowerCase() === '#ffffff' ? '1px solid var(--line)' : 'none',
        }}
    >
        {c.letter}
    </span>
);

// ----- form primitives used by the config-driven drawer -----
const Toggle = ({ value, onChange, disabled }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!value)}
        style={{
            width: 38, height: 22, borderRadius: 999, border: 0,
            background: value ? 'var(--accent)' : 'var(--bg-soft)',
            position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
        }}
    >
        <span style={{
            position: 'absolute', top: 2,
            left: value ? 18 : 2,
            width: 18, height: 18, background: 'white', borderRadius: 999,
            transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
    </button>
);

const Segmented = ({ value, options, onChange, disabled }) => (
    <div style={{
        display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8,
        padding: 2, background: 'var(--bg-soft)', opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
    }}>
        {options.map((o) => (
            <button
                key={o.value}
                type="button"
                onClick={() => onChange(o.value)}
                style={{
                    padding: '5px 10px', borderRadius: 6, border: 0, cursor: 'pointer',
                    background: value === o.value ? 'var(--bg-card)' : 'transparent',
                    color: 'var(--ink)', fontWeight: value === o.value ? 600 : 400,
                    fontSize: 12, fontFamily: 'inherit',
                    boxShadow: value === o.value ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}
            >
                {o.label}
            </button>
        ))}
    </div>
);

// Initialize state for one connector's config schema.
const initConfigState = (cfg) => {
    const state = {};
    cfg.sections.forEach((sec) => {
        sec.fields.forEach((f) => {
            if (f.type === 'list') {
                const o = {};
                f.items.forEach((it) => { o[it.id] = !!it.on; });
                state[f.key] = o;
            } else if (f.type === 'gdrive-drives' || f.type === 'gdrive-folders') {
                // Live-loaded selection maps. Default to an empty map; saved
                // selections come back from the persisted config on load.
                state[f.key] = {};
            } else {
                state[f.key] = f.on;
            }
        });
    });
    return state;
};

// Live Google Drive list of the user's drives (My Drive + each Shared Drive).
// Pulls from /api/integrations/google/drives. Each drive is toggleable; the
// on/off state lives in `state.drives` as { [driveId]: bool }.
const GDriveDrivesField = ({ fieldKey, state, set, token }) => {
    const [drives, setDrives] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        let cancel = false;
        axios.get('/api/integrations/google/drives', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (!cancel) setDrives(r.data || []); })
            .catch((e) => {
                if (cancel) return;
                const msg = (e.response && e.response.data && e.response.data.msg) || 'Failed to load Drives.';
                setErr(msg);
            });
        return () => { cancel = true; };
    }, [token]);

    if (err) return <div className="set-field" style={{ color: 'crimson', fontSize: 13 }}>{err}</div>;
    if (drives === null) return <div className="set-field" style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading Drives…</div>;
    if (drives.length === 0) return <div className="set-field" style={{ color: 'var(--ink-3)', fontSize: 13 }}>No Drives found on this account.</div>;

    const current = state[fieldKey] || {};
    return (
        <div className="set-field" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
            {drives.map((d) => (
                <div className="set-field" key={d.id} style={{ margin: 0 }}>
                    <div className="set-field-label">
                        <div className="lbl">{d.name}</div>
                        <div className="desc">{d.kind === 'mydrive' ? (d.email || 'My Drive') : 'Shared drive'}</div>
                    </div>
                    <div className="set-field-control">
                        <Toggle
                            value={!!current[d.id]}
                            onChange={(v) => set(fieldKey, { ...current, [d.id]: v })}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

// Lazy folder tree. Loads top-level folders for every enabled drive; expanding
// a folder fetches its children on demand. Selection state lives in
// `state.folders` as { [folderId]: bool }.
const GDriveFoldersField = ({ fieldKey, state, set, token }) => {
    const enabledDrives = useMemo(() => {
        const m = state.drives || {};
        return Object.keys(m).filter((k) => m[k]);
    }, [state.drives]);

    // childrenByParent: { [parentKey]: { loading, error, folders: [] } }
    // parentKey = driveId for the root of a drive, otherwise the folder id.
    const [childrenByParent, setChildren] = useState({});
    const [expanded, setExpanded] = useState({});
    const [driveNames, setDriveNames] = useState({});

    // Fetch drive names for headers.
    useEffect(() => {
        if (enabledDrives.length === 0) return;
        let cancel = false;
        axios.get('/api/integrations/google/drives', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                if (cancel) return;
                const m = {};
                (r.data || []).forEach((d) => { m[d.id] = d.name; });
                setDriveNames(m);
            })
            .catch(() => {});
        return () => { cancel = true; };
    }, [enabledDrives.length, token]);

    const loadChildren = React.useCallback((driveId, parentId, key) => {
        if (childrenByParent[key]) return;
        setChildren((m) => ({ ...m, [key]: { loading: true } }));
        const params = { driveId };
        if (parentId) params.parentId = parentId;
        const qs = new URLSearchParams(params).toString();
        axios.get(`/api/integrations/google/folders?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setChildren((m) => ({ ...m, [key]: { loading: false, folders: r.data || [] } })))
            .catch((e) => {
                const msg = (e.response && e.response.data && e.response.data.msg) || 'Failed to load folders.';
                setChildren((m) => ({ ...m, [key]: { loading: false, error: msg, folders: [] } }));
            });
    }, [childrenByParent, token]);

    // Auto-load top-level for each enabled drive.
    useEffect(() => {
        enabledDrives.forEach((driveId) => loadChildren(driveId, null, driveId));
    }, [enabledDrives, loadChildren]);

    if (enabledDrives.length === 0) {
        return (
            <div className="set-field" style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>
                Enable a Drive above to pick folders.
            </div>
        );
    }

    const selected = state[fieldKey] || {};
    const setSelected = (id, v) => set(fieldKey, { ...selected, [id]: v });

    const folderIcon = (
        <svg viewBox="0 0 16 16" width="14" height="14" fill="none" style={{ flexShrink: 0, marginRight: 6 }}>
            <path d="M2 4.5A1.5 1.5 0 013.5 3h2.4a1 1 0 01.7.3l.9.9a1 1 0 00.7.3H12.5A1.5 1.5 0 0114 6v5.5A1.5 1.5 0 0112.5 13h-9A1.5 1.5 0 012 11.5v-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    );

    const renderFolderNode = (driveId, folder, depth) => {
        const childKey = folder.id;
        const isOpen = expanded[childKey];
        const node = childrenByParent[childKey];
        const indent = depth * 16;
        return (
            <React.Fragment key={folder.id}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 6px',
                        paddingLeft: 6 + indent,
                        borderRadius: 6,
                        minHeight: 28,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => {
                            const next = !isOpen;
                            setExpanded((e) => ({ ...e, [childKey]: next }));
                            if (next) loadChildren(driveId, folder.id, childKey);
                        }}
                        style={{
                            border: 0, background: 'transparent', cursor: 'pointer',
                            color: 'var(--ink-3)', padding: 0, width: 16, height: 16,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontFamily: 'inherit', flexShrink: 0,
                        }}
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >
                        {isOpen ? '▾' : '▸'}
                    </button>
                    <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>{folderIcon}</span>
                    <span style={{
                        flex: 1, minWidth: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontSize: 13,
                    }}>
                        {folder.name}
                    </span>
                    <Toggle
                        value={!!selected[folder.id]}
                        onChange={(v) => setSelected(folder.id, v)}
                    />
                </div>
                {isOpen && node && node.loading && (
                    <div style={{ paddingLeft: 6 + indent + 22, color: 'var(--ink-3)', fontSize: 12, padding: '2px 0 2px 28px' }}>Loading…</div>
                )}
                {isOpen && node && node.error && (
                    <div style={{ paddingLeft: 6 + indent + 22, color: 'crimson', fontSize: 12 }}>{node.error}</div>
                )}
                {isOpen && node && node.folders && node.folders.map((ch) => renderFolderNode(driveId, ch, depth + 1))}
                {isOpen && node && node.folders && node.folders.length === 0 && !node.loading && !node.error && (
                    <div style={{ paddingLeft: 6 + indent + 28, color: 'var(--ink-3)', fontSize: 12, padding: '2px 0' }}>No sub-folders.</div>
                )}
            </React.Fragment>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
            {enabledDrives.map((driveId) => {
                const node = childrenByParent[driveId];
                return (
                    <div key={driveId}>
                        <div style={{
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            color: 'var(--ink-3)',
                            padding: '4px 6px',
                            borderBottom: '1px solid var(--line)',
                            marginBottom: 4,
                        }}>
                            {driveNames[driveId] || (driveId === 'root' ? 'My Drive' : 'Shared drive')}
                        </div>
                        {(!node || node.loading) && (
                            <div style={{ color: 'var(--ink-3)', fontSize: 12, padding: '6px 6px' }}>Loading folders…</div>
                        )}
                        {node && node.error && (
                            <div style={{ color: 'crimson', fontSize: 12, padding: '6px 6px' }}>{node.error}</div>
                        )}
                        {node && !node.loading && !node.error && node.folders && node.folders.length === 0 && (
                            <div style={{ color: 'var(--ink-3)', fontSize: 12, padding: '6px 6px' }}>No folders in this drive.</div>
                        )}
                        {node && !node.loading && !node.error && node.folders && node.folders.map((f) => renderFolderNode(driveId, f, 0))}
                    </div>
                );
            })}
        </div>
    );
};

const ConfigField = ({ field, state, set, token }) => {
    if (field.type === 'gdrive-drives') {
        return <GDriveDrivesField fieldKey={field.key} state={state} set={set} token={token} />;
    }
    if (field.type === 'gdrive-folders') {
        return <GDriveFoldersField fieldKey={field.key} state={state} set={set} token={token} />;
    }
    const disabled = field.dependsOn && !state[field.dependsOn];

    if (field.type === 'list') {
        const items = field.items && field.items.length ? field.items : [];
        if (items.length === 0) {
            return (
                <div className="set-field" style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>
                    {field.emptyHint || 'Nothing selected yet.'}
                </div>
            );
        }
        return (
            <div className="set-field" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                {items.map((it) => (
                    <div className="set-field" key={it.id} style={{ margin: 0 }}>
                        <div className="set-field-label">
                            <div className="lbl">{it.label}</div>
                            {it.sub && <div className="desc">{it.sub}</div>}
                        </div>
                        <div className="set-field-control">
                            <Toggle
                                value={!!(state[field.key] || {})[it.id]}
                                onChange={(v) => set(field.key, { ...(state[field.key] || {}), [it.id]: v })}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="set-field">
            <div className="set-field-label">
                <div className="lbl">{field.label}</div>
                {field.desc && <div className="desc">{field.desc}</div>}
            </div>
            <div className="set-field-control" style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : null}>
                {field.type === 'toggle' && (
                    <Toggle value={!!state[field.key]} onChange={(v) => set(field.key, v)} disabled={disabled} />
                )}
                {field.type === 'select' && (
                    <select
                        className="set-input"
                        value={state[field.key] ?? ''}
                        onChange={(e) => set(field.key, e.target.value)}
                        style={{ minWidth: 180 }}
                    >
                        {field.options.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                )}
                {field.type === 'text' && (
                    <input
                        type="text"
                        className="set-input"
                        value={state[field.key] ?? ''}
                        placeholder={field.placeholder}
                        onChange={(e) => set(field.key, e.target.value)}
                        style={{ minWidth: 220 }}
                    />
                )}
                {field.type === 'segmented' && (
                    <Segmented
                        value={state[field.key]}
                        options={field.options}
                        onChange={(v) => set(field.key, v)}
                        disabled={disabled}
                    />
                )}
            </div>
        </div>
    );
};

const SourceDrawer = ({ connector, mode, token, onClose, onConnect, onDisconnect }) => {
    const c = connector;
    const cfg = CONNECTOR_CONFIG[c.key] || FALLBACK_CONFIG;
    const [state, setState] = useState(() => initConfigState(cfg));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [account, setAccount] = useState({ label: '', sub: '', connected: false });
    const [error, setError] = useState(null);
    const [figmaKey, setFigmaKey] = useState(null); // { key, name, createdAt } for the Figma plugin
    const [copied, setCopied] = useState(false);
    const set = (k, v) => setState((s) => ({ ...s, [k]: v }));
    const connecting = mode === 'connect';

    // Load saved config + account info for this provider.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        axios
            .get(`/api/dashboard/integrations/${c.key}/config`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                if (cancelled) return;
                const saved = (r.data && r.data.config) || {};
                // Merge saved values on top of schema defaults so newly added fields keep defaults.
                setState((init) => ({ ...init, ...saved }));
                setAccount({
                    label: r.data.accountLabel || '',
                    sub: r.data.accountSub || '',
                    connected: !!r.data.connected,
                });
                if (r.data.figmaKey) setFigmaKey(r.data.figmaKey);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setError('Failed to load saved configuration.');
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [c.key, token]);

    const handleSave = () => {
        setSaving(true);
        setError(null);
        axios
            .put(
                `/api/dashboard/integrations/${c.key}/config`,
                { config: state },
                { headers: { Authorization: `Bearer ${token}` } },
            )
            .then(() => { setSaving(false); onClose(); })
            .catch(() => {
                setSaving(false);
                setError('Failed to save configuration.');
            });
    };

    const handleAuthorize = () => {
        const ep = AUTH_ENDPOINTS[c.key];
        if (!ep) {
            setError(`OAuth for ${c.name} is not yet available. Save your configuration; the connect flow will be enabled in a future release.`);
            return;
        }
        setError(null);

        // Figma: generate a personal API key for the plugin instead of doing
        // an OAuth redirect. The key is returned once and shown to the user.
        if (ep.kind === 'apikey') {
            axios
                .post('/api/keys', { name: 'Figma Plugin Key' }, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => {
                    setFigmaKey(r.data);
                    setAccount({
                        label: r.data.name || 'Figma Plugin Key',
                        sub: 'Plugin key generated',
                        connected: true,
                    });
                })
                .catch((err) => {
                    if (err.response && err.response.status === 402) {
                        setError('App limit reached. Upgrade your plan to connect Figma.');
                    } else {
                        setError('Failed to generate Figma plugin key.');
                    }
                });
            return;
        }

        const url = ep.tokenInQuery
            ? `${ep.path}?token=${encodeURIComponent(token)}`
            : ep.path;
        axios
            .get(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                if (r.data && r.data.url) {
                    window.location.href = r.data.url;
                } else {
                    setError('Authorization URL was not returned by the server.');
                }
            })
            .catch((err) => {
                const msg = (err.response && err.response.data && (err.response.data.msg || err.response.data.error)) || 'Failed to start authorization.';
                setError(msg);
            });
    };

    const copyFigmaKey = () => {
        if (!figmaKey || !figmaKey.key) return;
        try {
            navigator.clipboard.writeText(figmaKey.key);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            setError('Could not copy to clipboard.');
        }
    };

    const handleDisconnect = () => {
        axios
            .delete(`/api/dashboard/integrations/${c.key}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => onDisconnect())
            .catch(() => setError('Failed to disconnect.'));
    };

    return (
        <div className="src-drawer-wrap" onMouseDown={(e) => { if (e.target.classList.contains('src-drawer-wrap')) onClose(); }}>
            <div className="src-drawer">
                <div className="src-drawer-h">
                    <SourceTile c={c} size="lg" />
                    <div className="meta">
                        <b>{connecting ? `Connect ${c.name}` : c.name}</b>
                        <span>{c.cat} · {c.kind}</span>
                    </div>
                    <button type="button" className="src-drawer-x" onClick={onClose} aria-label="Close">{ICN.x}</button>
                </div>

                <div className="src-drawer-body">
                    {loading ? (
                        <div style={{ padding: 24, color: 'var(--ink-3)' }}>Loading configuration…</div>
                    ) : (
                        <>
                            <div className="set-block">
                                <div className="set-block-head"><h3>Connection</h3></div>
                                {connecting || !account.connected ? (
                                    <div className="src-auth">
                                        {c.key === 'figma' ? (
                                            <p>Generate a personal API key to paste into the Rush Studio plugin inside Figma. The plugin uses this key to push and pull text layers from your files.</p>
                                        ) : (
                                            <p>Authorize Rush Studio to read and write content in your {c.name} account. You'll be redirected to {c.name} to grant access.</p>
                                        )}
                                        <button type="button" className="set-btn accent" style={{ alignSelf: 'flex-start' }} onClick={handleAuthorize}>
                                            {c.key === 'figma' ? 'Generate plugin key' : `Authorize with ${c.name}`}
                                        </button>
                                        {!AUTH_ENDPOINTS[c.key] && (
                                            <p style={{ marginTop: 8, color: 'var(--ink-3)', fontSize: 12 }}>
                                                OAuth for {c.name} isn't enabled yet — configuration can be saved now and connected once the backend route is live.
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="set-list-row" style={{ padding: '14px 0' }}>
                                            <div className="set-avatar"><SourceTile c={c} /></div>
                                            <div className="li-main">
                                                <b>{account.label ? (c.key === 'figma' ? account.label : `Connected as ${account.label}`) : 'Connected'}</b>
                                                {account.sub && <span>{account.sub}</span>}
                                            </div>
                                            <div className="grow" />
                                            {c.key === 'figma'
                                                ? <button type="button" className="set-btn sm" onClick={handleAuthorize}>Regenerate</button>
                                                : <button type="button" className="set-btn sm">Reauthorize</button>}
                                        </div>
                                        {c.key === 'figma' && figmaKey && (
                                            <div style={{
                                                marginTop: 6,
                                                background: 'var(--bg-soft, #f6f5f1)',
                                                border: '1px solid var(--line, #e5e3dd)',
                                                borderRadius: 10,
                                                padding: '10px 12px',
                                            }}>
                                                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>
                                                    Plugin key — paste this into the Rush Studio plugin in Figma.
                                                </div>
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                                                }}>
                                                    <code style={{
                                                        flex: 1, minWidth: 0,
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                        fontSize: 12,
                                                        background: 'var(--bg-card, white)',
                                                        border: '1px solid var(--line, #e5e3dd)',
                                                        borderRadius: 6,
                                                        padding: '6px 8px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {figmaKey.key}
                                                    </code>
                                                    <button type="button" className="set-btn sm" onClick={copyFigmaKey}>
                                                        {copied ? 'Copied!' : 'Copy'}
                                                    </button>
                                                </div>
                                                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
                                                    Treat this like a password. Disconnect to revoke it.
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {cfg.sections.map((sec, i) => (
                                <div className="set-block" key={i}>
                                    <div className={`set-block-head${sec.desc ? ' flush' : ''}`}>
                                        <h3>{sec.title}</h3>
                                        {sec.desc && <p>{sec.desc}</p>}
                                    </div>
                                    {sec.fields.map((f) => (
                                        <ConfigField key={f.key} field={f} state={state} set={set} token={token} />
                                    ))}
                                </div>
                            ))}

                            {error && (
                                <div style={{ padding: '8px 12px', color: 'crimson', fontSize: 13 }}>{error}</div>
                            )}
                        </>
                    )}
                </div>

                <div className="src-drawer-foot">
                    {!connecting && account.connected && (
                        <button type="button" className="set-btn danger" onClick={handleDisconnect}>Disconnect</button>
                    )}
                    <div className="grow" />
                    <button type="button" className="set-btn" onClick={onClose} disabled={saving}>Cancel</button>
                    <button type="button" className="set-btn accent" onClick={handleSave} disabled={loading || saving}>
                        {ICN.check} {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ConnectorCard = ({ c, connected, status, onClick }) => (
    <button
        type="button"
        className="src-card"
        onClick={onClick}
        style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: 14,
            textAlign: 'left',
            cursor: 'pointer',
            color: 'inherit',
            fontFamily: 'inherit',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
        }}
    >
        <SourceTile c={c} size="lg" />
        <div className="src-card-body" style={{ flex: 1, minWidth: 0 }}>
            <b>{c.name}</b>
            <span>{c.cat} · {c.kind}</span>
            <div style={{ marginTop: 8, fontSize: 12, color: connected ? 'var(--green)' : 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
                {connected && <span style={{ width: 6, height: 6, borderRadius: 50, background: 'var(--green)' }} />}
                {status || c.blurb}
            </div>
        </div>
    </button>
);

// ---------- Row variants matching the new design ----------
const ConnectedRow = ({ c, info, onConfigure, onToggle }) => {
    const isSyncing = (info?.status || '').toLowerCase().includes('sync');
    const enabled = info?.enabled !== false;
    const label =
        info?.summary
        || (info?.fileCount != null && `${info.fileCount} files · synced ${info.lastSync || 'recently'}`)
        || c.blurb;

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '14px 16px',
                borderBottom: '1px solid var(--line)',
            }}
        >
            <SourceTile c={c} size="lg" />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{label}</div>
            </div>
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 500,
                    background: isSyncing ? '#fbe8de' : '#e3f5ea',
                    color: isSyncing ? '#b85a2a' : '#1f7a45',
                }}
            >
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: isSyncing ? '#d97757' : '#2faa64',
                    }}
                />
                {isSyncing ? 'Syncing' : 'Connected'}
            </span>
            <button
                type="button"
                onClick={onConfigure}
                style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 13,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                }}
            >
                Configure
            </button>
            <button
                type="button"
                onClick={onToggle}
                aria-label={enabled ? 'Disable' : 'Enable'}
                style={{
                    width: 40,
                    height: 22,
                    borderRadius: 999,
                    border: 0,
                    position: 'relative',
                    background: enabled ? 'var(--accent, #d97757)' : 'rgba(0,0,0,0.18)',
                    cursor: 'pointer',
                    padding: 0,
                }}
            >
                <span
                    style={{
                        position: 'absolute',
                        top: 2,
                        left: enabled ? 20 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: 999,
                        background: '#fff',
                        transition: 'left 0.15s',
                    }}
                />
            </button>
        </div>
    );
};

const AvailableRow = ({ c, atLimit, onClick }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 12,
            border: '1px solid var(--line)',
            borderRadius: 12,
            background: 'var(--bg-card)',
        }}
    >
        <SourceTile c={c} size="lg" />
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{c.name}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{c.blurb}</div>
        </div>
        <button
            type="button"
            onClick={onClick}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                color: 'var(--accent, #d97757)',
                border: '1px solid var(--accent, #d97757)',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
            }}
            title={atLimit ? 'Upgrade required' : `Add ${c.name}`}
        >
            <svg viewBox="0 0 14 14" width="12" height="12" fill="none" aria-hidden="true">
                <rect x="3" y="6" width="8" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
                <path d="M5 6V4.5a2 2 0 0 1 4 0V6" stroke="currentColor" strokeWidth="1.4" fill="none" />
            </svg>
            Add connector
        </button>
    </div>
);

// ---------- Plan-limit banner ----------
const PlanLimitBanner = ({ plan, onUpgrade }) => {
    if (!plan || plan.unlimited) return null;
    const pct = plan.limit > 0 ? Math.min(100, Math.round((plan.used / plan.limit) * 100)) : 0;
    const atLimit = plan.atLimit;
    const bg = atLimit ? 'var(--accent-soft, #fbe8de)' : 'var(--bg-soft, #f6f5f1)';
    const border = atLimit ? 'var(--accent, #d97757)' : 'var(--line, #e5e3dd)';
    const accent = 'var(--accent, #d97757)';
    return (
        <div style={{
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: 14,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginBottom: 16,
            flexWrap: 'wrap',
        }}>
            <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 14 }}>
                    <span style={{ color: accent, fontSize: 16, lineHeight: 1 }}>⚡</span>
                    <b>{plan.planLabel} plan</b>
                    <span style={{ color: 'var(--ink-3)' }}>
                        {plan.used} of {plan.limit} connectors · <b>{plan.remaining} left</b>
                    </span>
                </div>
                <div style={{
                    height: 5, background: 'rgba(0,0,0,0.06)', borderRadius: 999, overflow: 'hidden', maxWidth: 520,
                }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: accent, transition: 'width 0.3s' }} />
                </div>
                {atLimit && (
                    <div style={{ color: accent, fontSize: 13, marginTop: 8, fontWeight: 500 }}>
                        You've reached your {plan.planLabel} limit. Upgrade to connect more tools.
                    </div>
                )}
            </div>
            {plan.nextPlanLabel && (
                <button
                    type="button"
                    onClick={onUpgrade}
                    style={{
                        background: 'var(--ink, #0e0e10)',
                        color: 'white',
                        border: 0,
                        borderRadius: 8,
                        padding: '10px 16px',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Upgrade to {plan.nextPlanLabel}
                </button>
            )}
        </div>
    );
};

const Sources = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [integrations, setIntegrations] = useState([]);
    const [plan, setPlan] = useState(null);
    const [cat, setCat] = useState('All');
    const [drawer, setDrawer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const reload = React.useCallback(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get('/api/dashboard/integrations', { headers }),
            axios.get('/api/dashboard/integrations/plan', { headers }).catch(() => ({ data: null })),
        ])
            .then(([rIntegrations, rPlan]) => {
                setIntegrations(Array.isArray(rIntegrations.data) ? rIntegrations.data : []);
                setPlan(rPlan.data || null);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load integrations.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    useEffect(() => { reload(); }, [reload]);

    const connectedByKey = useMemo(() => {
        const map = {};
        integrations.forEach((i) => { map[i.key] = i; });
        return map;
    }, [integrations]);

    const filtered = useMemo(
        () => CONNECTORS.filter((c) => cat === 'All' || c.cat === cat),
        [cat],
    );
    const connected = filtered.filter((c) => connectedByKey[c.key] && connectedByKey[c.key].connected);
    const available = filtered.filter((c) => !connectedByKey[c.key] || !connectedByKey[c.key].connected);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading sources…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Connected sources</h1>
                <span className="rs-page-title-count">{connected.length}</span>
            </div>
            <p className="rs-page-sub">Pull source content from external tools — and push translations back to where they live.</p>

            <PlanLimitBanner plan={plan} onUpgrade={() => navigate('/dashboard/account?tab=billing')} />

            <div className="src-stats">
                <div className="src-stat">
                    <div className="v">{connected.length}<span className="u">connected</span></div>
                    <div className="l">Active sources</div>
                </div>
                <div className="src-stat">
                    <div className="v">{CONNECTORS.length}<span className="u">total</span></div>
                    <div className="l">Available connectors</div>
                </div>
                <div className="src-stat">
                    <div className="v">{new Set(connected.map((c) => c.cat)).size}</div>
                    <div className="l">Categories in use</div>
                </div>
                <div className="src-stat">
                    <div className="v">—</div>
                    <div className="l">Last sync</div>
                </div>
            </div>

            <div className="rs-views">
                {CATEGORIES.map((c) => (
                    <button
                        key={c}
                        type="button"
                        className={`rs-view-tab ${cat === c ? 'on' : ''}`}
                        onClick={() => setCat(c)}
                    >
                        {c}
                    </button>
                ))}
            </div>

            <div className="rs-card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
                {connected.length === 0 ? (
                    <div style={{ color: 'var(--ink-3)', padding: 20 }}>
                        Nothing connected yet. Pick one below to get started.
                    </div>
                ) : (
                    connected.map((c, idx) => {
                        const info = connectedByKey[c.key];
                        return (
                            <div
                                key={c.key}
                                style={idx === connected.length - 1 ? { borderBottom: 0 } : undefined}
                            >
                                <ConnectedRow
                                    c={c}
                                    info={info}
                                    onConfigure={() => setDrawer({ connector: c, mode: 'configure' })}
                                    onToggle={() => setDrawer({ connector: c, mode: 'configure' })}
                                />
                            </div>
                        );
                    })
                )}
            </div>

            <div className="rs-card">
                <div className="rs-card-h" style={{ display: 'block', borderBottom: '1px solid var(--line)', paddingBottom: 12, marginBottom: 16 }}>
                    <h3 style={{ margin: 0 }}>Available</h3>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
                        Browse the full catalog for 40+ more integrations.
                    </div>
                </div>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: 12,
                    }}
                >
                    {available.map((c) => (
                        <AvailableRow
                            key={c.key}
                            c={c}
                            atLimit={!!(plan && plan.atLimit)}
                            onClick={() => {
                                if (plan && plan.atLimit) {
                                    navigate('/dashboard/account?tab=billing');
                                } else {
                                    setDrawer({ connector: c, mode: 'connect' });
                                }
                            }}
                        />
                    ))}
                </div>
            </div>

            {drawer && (
                <SourceDrawer
                    connector={drawer.connector}
                    mode={drawer.mode}
                    token={token}
                    onClose={() => { setDrawer(null); reload(); }}
                    onConnect={() => { setDrawer(null); reload(); }}
                    onDisconnect={() => { setDrawer(null); reload(); }}
                />
            )}
        </>
    );
};

export default Sources;
